/**
 * The diary's own recent shape, read fresh for a mood computation.
 *
 * This file's whole job is turning Dexie into the dense day series
 * `packages/shared/src/voice-facts.ts` reduces — the arithmetic itself lives
 * there, shared verbatim with the push server, so the app and the lock screen
 * cannot come to different conclusions about the same week. See
 * `packages/shared/src/mood.ts` for what these numbers feed into.
 *
 * Deliberately not cached, unlike sass memory: these are small, indexed local
 * reads, and a mood that lagged behind a log written thirty seconds ago would
 * be the one part of this system worth distrusting.
 */

import {
  addDays,
  foldLt,
  type GoalPhase,
  type HerLifeFacts,
  isAppAnniversary,
  isBirthday,
  isHolidayToday,
  isKateBirthday,
  isStruggling,
  type MoodInputs,
  phaseFromTarget,
  restingEnergy,
  startOfWeek,
  VOICE_WINDOW_DAYS,
  type VoiceDay,
  type VoiceFacts,
  voiceFacts,
  weeklyWhim,
  weightTrendPerWeek,
} from '@gramas/shared'
import { db } from '../db/dexie.js'
import {
  bodyMeasurementsBetween,
  entriesForDay,
  goalForDate,
  goalPicker,
  latestMeasurements,
  totalsFor,
} from '../db/repo.js'
import { currentProfile } from '../screens/useProfile.js'

/** No real streak runs longer than this; the cap is a scan backstop, not a limit. */
const STREAK_SCAN_CAP_DAYS = 400
/** Trailing days the repeat-food callback looks over. */
const REPEAT_WINDOW_DAYS = 7
/**
 * How much weight history the cut-stall check reads. `weightTrendPerWeek`
 * wants three points spanning a fortnight before it will answer at all, and
 * the stall it is looking for is defined over about three weeks — four weeks
 * of measurements is the smallest window that reliably produces a number.
 */
const WEIGHT_WINDOW_DAYS = 28

/** Every day in the window, gaps included, ending on `date`. */
async function daySeries(date: string, windowDays: number): Promise<VoiceDay[]> {
  const from = addDays(date, -(windowDays - 1))
  const [rows, goalOn] = await Promise.all([
    db.logEntries.where('logDate').between(from, date, true, true).toArray(),
    goalPicker(),
  ])
  const kcalByDate = new Map<string, number>()
  for (const row of rows) {
    if (row.deletedAt != null) continue
    kcalByDate.set(row.logDate, (kcalByDate.get(row.logDate) ?? 0) + row.kcal)
  }
  return Array.from({ length: windowDays }, (_, i) => {
    const day = addDays(from, i)
    return { date: day, kcal: kcalByDate.get(day) ?? 0, kcalTarget: goalOn(day).kcalTarget }
  })
}

/**
 * Everything the mood needs, in one pass over the last month of the diary.
 *
 * The window is a month rather than a week because two of the facts want the
 * longer view: how long a silence has run (up to the cap that stops a diary
 * abandoned for a year from being scanned to find out), and how this weekday
 * has historically gone.
 */
export async function voiceFactsFor(date: string): Promise<VoiceFacts> {
  return voiceFacts(await daySeries(date, VOICE_WINDOW_DAYS))
}

/** Over-budget days in the 7 ending on `date`, inclusive. */
export async function overDaysInWeek(date: string): Promise<number> {
  return (await voiceFactsFor(date)).overDaysInWeek
}

/**
 * 0 if `date` itself has an entry; otherwise how many days back the most
 * recent one is, capped so a diary abandoned for months does not make the
 * caller scan its whole history to find that out.
 */
export async function daysSinceLastLog(date: string): Promise<number> {
  return (await voiceFactsFor(date)).daysSinceLastLog
}

/** A streak of 7+ days that was alive as of yesterday and is gone as of `date`. */
export async function streakRecentlyBroken(date: string): Promise<boolean> {
  return (await voiceFactsFor(date)).streakRecentlyBroken
}

/**
 * Consecutive logged days counting back from `date`, unbounded — well past the
 * month the mood window reads, so the trophy cabinet can recognise a 100-day
 * run. `date` itself is allowed to be empty without breaking the count, same
 * rule as `loggingStreak`.
 */
export async function currentStreak(date: string): Promise<number> {
  const from = addDays(date, -STREAK_SCAN_CAP_DAYS)
  const rows = await db.logEntries.where('logDate').between(from, date, true, true).toArray()
  const logged = new Set(
    rows.filter((r) => r.deletedAt == null && r.kcal > 0).map((r) => r.logDate),
  )
  let streak = 0
  let cursor = logged.has(date) ? date : addDays(date, -1)
  for (let i = 0; i < STREAK_SCAN_CAP_DAYS; i++) {
    if (!logged.has(cursor)) break
    streak++
    cursor = addDays(cursor, -1)
  }
  return streak
}

/**
 * Kilograms per week over the last four weeks, or null when there is not
 * enough on the scale to say.
 *
 * Read on every log commit rather than cached for the day: the query is one
 * indexed range over a table with at most a few hundred rows, and loyal mode's
 * cut-stall trigger is the one part of the mood a user would notice lagging —
 * weighing in and immediately being told the cut is stalled is worse than the
 * extra read.
 */
export async function weightTrendKgWk(date: string): Promise<number | null> {
  const rows = await bodyMeasurementsBetween(addDays(date, -(WEIGHT_WINDOW_DAYS - 1)), date)
  const points = rows.flatMap((row) =>
    row.weightKg == null ? [] : [{ date: row.measuredOn, weightKg: row.weightKg }],
  )
  return weightTrendPerWeek(points)
}

/**
 * Everything `computeMood` needs except the one number its callers disagree
 * about.
 *
 * Three places ask what mood she is in — a log commit, the presence
 * controller, and the push server — and all three used to assemble the
 * ingredients themselves. Two of them drifted: the anniversary of the account
 * was written into `calendar.ts`, listed in PERSONA.md as a festive trigger,
 * and then never passed by anybody. Assembling it once fixes that class of
 * bug rather than that instance of it.
 *
 * `daysSinceLastLog` is deliberately left out of the returned inputs and
 * supplied by the caller: a log commit has to answer 0 no matter what the
 * diary looked like a second earlier, because one just landed.
 */
export interface MoodBasis extends Omit<MoodInputs, 'daysSinceLastLog'> {
  /** What the diary has actually been doing, for callers that want more. */
  facts: VoiceFacts
  phase?: GoalPhase
  weightTrendKgWk: number | null
}

export async function moodBasisFor(date: string): Promise<MoodBasis> {
  const profile = currentProfile()
  const [facts, trendKgWk, goal, body] = await Promise.all([
    voiceFactsFor(date),
    weightTrendKgWk(date),
    goalForDate(date),
    latestMeasurements(date),
  ])
  const resting = restingEnergy(
    {
      weightKg: body.weightKg,
      heightCm: profile?.heightCm ?? null,
      birthDate: profile?.birthDate ?? null,
      sex: profile?.sex ?? null,
      activityFactor: profile?.activityFactor ?? null,
    },
    date,
  )
  const phase = resting.tdee == null ? undefined : phaseFromTarget(goal.kcalTarget, resting.tdee)
  const now = new Date()

  return {
    facts,
    phase,
    weightTrendKgWk: trendKgWk,
    struggling: isStruggling({
      phase,
      overDaysInWeek: facts.overDaysInWeek,
      streakRecentlyBroken: facts.streakRecentlyBroken,
      weightTrendKgWk: trendKgWk,
    }),
    isFestiveToday:
      isHolidayToday(now) ||
      isBirthday(now, profile?.birthDate) ||
      isKateBirthday(now) ||
      isAppAnniversary(now, profile?.createdAt),
    isNovember: now.getMonth() === 10,
    weeklyWhim: weeklyWhim(profile?.id ?? 'anon', startOfWeek(date)),
  }
}

/**
 * Yesterday, as the three facts she might have an opinion about.
 *
 * A finished day on purpose — see `HerLifeFacts`. Reading today would make her
 * own day change under her as the day went on, and would put the app and the
 * lock screen on different answers depending on what time each was asked.
 */
export async function herLifeFactsFor(date: string): Promise<HerLifeFacts> {
  const yesterday = addDays(date, -1)
  const [entries, goal] = await Promise.all([entriesForDay(yesterday), goalForDate(yesterday)])
  const totals = totalsFor(entries)
  return {
    yesterdayEmpty: entries.length === 0,
    yesterdayProteinHit:
      goal.proteinG != null && goal.proteinG > 0 && totals.protein >= goal.proteinG,
    yesterdayOver: totals.kcal > goal.kcalTarget,
  }
}

/**
 * How many times this same thing has been logged in the 7 days ending on
 * `date`, this commit included.
 *
 * Matched on the folded snapshot name, which is what makes "Varškės sūrelis"
 * and "varskes surelis" the same habit — a repeat callback that missed because
 * one of them was typed by hand would read as her not paying attention. Names
 * are compared whole rather than stemmed: two genuinely different products
 * sharing a head noun are not the same lunch.
 */
export async function repeatCountThisWeek(name: string, date: string): Promise<number> {
  const target = foldLt(name.trim())
  if (target === '') return 0
  const from = addDays(date, -(REPEAT_WINDOW_DAYS - 1))
  const rows = await db.logEntries.where('logDate').between(from, date, true, true).toArray()
  return rows.filter((row) => row.deletedAt == null && foldLt(row.snapshotName.trim()) === target)
    .length
}
